import { useState } from 'react';
import type { TableProps } from 'antd';
import { Switch } from 'antd';
import { AlertOutlined, DeleteOutlined, EditOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons';

import { DataTable, MenuThaoTac } from '../../../components/shared';
import { Badge, Button, Empty } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { SO_KENH_HIEN, TONG_KENH } from '../constants';
import { useBatTatTacVu, useDanhMuc, useDanhSachKenh, useDanhSachTacVu, useXoaTacVu } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import { dieuKienCuaTacVu } from '../nghiep-vu/canh-bao';
import type { NotifyAlarmTask, PhamViCanhBao } from '../types';
import { HopTacVu } from './HopTacVu';
import { HopXacNhan } from './HopXacNhan';
import { VungLoi } from './VungLoi';

/**
 * Tab "Danh sách cảnh báo" (aaPanel: Alarm List): tiêu đề, bật/tắt, kênh, điều kiện, kiểm lần cuối, số lần đã gửi.
 * Hành động trên dòng gom vào menu `⋮`; thiếu quyền sửa thì ẩn mục, không hiện mờ.
 */
export function DanhSachTacVu({
  pv,
  duocSua,
  onXemLichSu,
  onMoCauHinhKenh,
}: {
  pv: PhamViCanhBao;
  duocSua: boolean;
  onXemLichSu: (taskId: string) => void;
  onMoCauHinhKenh: () => void;
}) {
  const { t, locale } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const danhMuc = useDanhMuc(pv);
  const kenh = useDanhSachKenh(pv);
  const tacVu = useDanhSachTacVu(pv);
  const batTat = useBatTatTacVu(pv);
  const xoa = useXoaTacVu(pv);
  const [hop, setHop] = useState<{
    tacVu: NotifyAlarmTask | null;
    lan: number;
  } | null>(null);
  const [xnXoa, setXnXoa] = useState<NotifyAlarmTask | null>(null);

  const loaiTheoMa = new Map((danhMuc.data?.alarm_types ?? []).map((l) => [l.code, l]));
  const kenhTheoId = new Map((kenh.data ?? []).map((k) => [k.id, k]));
  const dangTai = tacVu.isLoading;
  const moThem = () => setHop((h) => ({ tacVu: null, lan: (h?.lan ?? 0) + 1 }));

  const columns: TableProps<NotifyAlarmTask>['columns'] = [
    {
      title: t('canhBao.cotTieuDe'),
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-xs flex-col">
          <span className="truncate text-sm font-medium text-content-primary" title={row.title}>
            {row.title}
          </span>
          <span className="truncate text-2xs text-content-muted">{dich.tenLoai(row.type_code)}</span>
        </div>
      ),
    },
    {
      title: t('canhBao.cotBat'),
      width: 80,
      render: (_v, row) =>
        // Thiếu quyền sửa thì nói trạng thái bằng nhãn, không hiện công tắc mờ (ADR-UI-0005).
        !duocSua ? (
          <Badge variant={row.enabled ? 'success' : 'neutral'}>
            {row.enabled ? t('canhBaoKenh.dangBat') : t('canhBaoKenh.dangTat')}
          </Badge>
        ) : (
          <Switch
            size="small"
            checked={row.enabled}
            loading={batTat.isPending && batTat.variables?.id === row.id}
            aria-label={t('canhBao.batTatNhan', { ten: row.title })}
            onChange={() => batTat.mutate(row)}
          />
        ),
    },
    {
      title: t('canhBao.cotKenh'),
      width: 220,
      render: (_v, row) => {
        const ds = row.channel_ids.map((id) => kenhTheoId.get(id)).filter((k) => k !== undefined);
        if (ds.length === 0) return <span className="text-xs text-warning-700">{t('canhBao.kenhDaGo')}</span>;
        const conLai = ds.length - SO_KENH_HIEN;
        return (
          <span className="flex min-w-0 flex-wrap gap-1">
            {ds.slice(0, SO_KENH_HIEN).map((k) => (
              <Badge key={k.id} variant={TONG_KENH}>
                <span className="block max-w-[10rem] truncate" title={`${dich.tenKenh(k.channel_type)} · ${k.name}`}>
                  {k.name}
                </span>
              </Badge>
            ))}
            {conLai > 0 && (
              <span
                className="text-2xs text-content-muted"
                title={ds
                  .slice(SO_KENH_HIEN)
                  .map((k) => k.name)
                  .join(', ')}
              >
                {t('canhBao.themSo', { so: conLai })}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: t('canhBao.cotDieuKien'),
      width: 220,
      render: (_v, row) => {
        const loai = loaiTheoMa.get(row.type_code);
        const dk = loai ? dieuKienCuaTacVu(row, loai.params) : [];
        if (dk.length === 0) return <span className="text-xs text-content-muted">{t('canhBao.moiLanXayRa')}</span>;
        return (
          <ul className="space-y-0.5">
            {dk.map((d) => (
              <li key={d.key} className="text-xs text-content-secondary">
                {dich.nhanThamSo(d.key)}: <span className="font-medium">{dich.nhanTuyChon(d.giaTri)}</span>
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      title: t('canhBao.cotKiemCuoi'),
      width: 150,
      render: (_v, row) => {
        if (loaiTheoMa.get(row.type_code)?.trigger === 'EVENT') {
          return <span className="text-xs text-content-muted">{t('canhBao.theoSuKien')}</span>;
        }
        return (
          <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
            {row.last_check_at ? ngayGioVN(row.last_check_at, locale) : KHONG_CO}
          </span>
        );
      },
    },
    {
      title: t('canhBao.cotDaGui'),
      width: 100,
      render: (_v, row) => (
        <Button variant="link" size="sm" onClick={() => onXemLichSu(row.id)}>
          {t('canhBao.soLan', { so: soVN(row.total_sent, locale) })}
        </Button>
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={row.title}
          muc={[
            {
              khoa: 'sua',
              nhan: t('canhBao.suaTacVu'),
              icon: <EditOutlined />,
              hien: duocSua,
              onChon: () => setHop((h) => ({ tacVu: row, lan: (h?.lan ?? 0) + 1 })),
            },
            {
              khoa: 'lich-su',
              nhan: t('canhBao.xemLichSu'),
              icon: <HistoryOutlined />,
              onChon: () => onXemLichSu(row.id),
            },
            {
              khoa: 'xoa',
              nhan: t('canhBao.xoaTacVu'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              hien: duocSua,
              onChon: () => {
                xoa.reset();
                setXnXoa(row);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-3">
      {duocSua && (
        <div className="flex justify-end">
          <Button variant="primary" size="md" icon={<PlusOutlined />} disabled={dangTai} onClick={moThem}>
            {t('canhBao.themTacVu')}
          </Button>
        </div>
      )}
      <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
        {tacVu.error && !tacVu.data ? (
          <VungLoi cau={t('canhBao.loiTacVu', { loi: dichLoi(tacVu.error) })} onThuLai={() => void tacVu.refetch()} />
        ) : (
          <DataTable<NotifyAlarmTask>
            caption={t('canhBao.bangTacVu')}
            rowKey="id"
            dataSource={tacVu.data ?? []}
            columns={columns}
            loading={dangTai}
            size="middle"
            pagination={false}
            locale={{
              emptyText: dangTai ? (
                ' '
              ) : (
                <Empty
                  icon={AlertOutlined}
                  title={t('canhBao.rongTacVu')}
                  description={t('canhBao.rongTacVuMo')}
                  action={
                    duocSua ? (
                      <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={moThem}>
                        {t('canhBao.themTacVuDauTien')}
                      </Button>
                    ) : undefined
                  }
                />
              ),
            }}
          />
        )}
      </div>

      {hop && (
        <HopTacVu
          key={hop.lan}
          pv={pv}
          loaiCanhBao={danhMuc.data?.alarm_types ?? []}
          loaiKenh={danhMuc.data?.channel_types ?? []}
          kenh={kenh.data ?? []}
          dangCo={hop.tacVu}
          onClose={() => setHop(null)}
          onMoCauHinhKenh={() => {
            setHop(null);
            onMoCauHinhKenh();
          }}
        />
      )}

      <HopXacNhan
        open={xnXoa !== null}
        tieuDe={t('canhBao.xnXoaTieuDe', { ten: xnXoa?.title ?? '' })}
        than={t('canhBao.xnXoaThan')}
        nhanXacNhan={t('canhBao.xoaTacVu')}
        nhanDangChay={t('canhBao.dangXoa')}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onClose={() => setXnXoa(null)}
        onXacNhan={() => xnXoa && xoa.mutate(xnXoa, { onSuccess: () => setXnXoa(null) })}
      />
    </div>
  );
}
